import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Hls from 'hls.js';
import { useSearchParams } from 'react-router-dom';
import { useNode } from '../contexts/NodeContext';
import {
  getNodeCameras,
  getNodeGroups,
  getRecordingDates,
  getRecordingSegments,
  getRecordingTriggers,
  getRecordingConfig,
  getRecordingConfigs,
  getRecordingStorageByCamera,
  updateRecordingConfig,
} from '../services/api.service';
import {
  VideoCameraIcon,
  ArrowLeftIcon,
  FilmIcon,
  ArrowDownTrayIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import {
  RecordingCameraCard, ListPager, isCameraOnline, IMPLICIT_RECORDING_CONFIG,
  MultiCameraTimeline, TimeRangePicker, ClockReadout, DateStepper, TransportCluster, jumpPointsFor, jumpTarget,
  usePlaybackKeys, JUMP_LEAD_MS, nudgeStalledSeek, footageRangeOf, Tip, type TileStatus,
  HighSpeedBufferGate, applyRateToHls, attachNextSegmentWarmer, rateBufferConfig, type GateState,
  isTrickRate, trickChunkUrl, switchRendition, PlaybackCameraPanel,
  RecordingSegmentsPanel, useStoredToggle,
  PageTitle, PageWithFilters, FilterColumn, FilterSection, FilterSearch, FilterCheck, useFilterColumn,
} from '@trinetra/shared';

const API_BASE = window.location.origin + '/api';

// The player's side panels folded to rails — per viewer, this browser only.
const PANEL_KEY = 'trinetra.recordings.panelCollapsed';
const SEGMENTS_PANEL_KEY = 'trinetra.recordings.segmentsCollapsed';

/* ─── helpers ─── */
const fmtTime = (iso: string | Date) => {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};
const fmtDate = (dateStr: string) => {
  try {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d); // local midnight
    return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return dateStr; }
};

const SPEEDS = [0.5, 1, 2, 4, 8, 16];

// A stale in-progress row (recorder died before finalizing) must not create a
// phantom live tail — mirrors the local backend's LIVE_TAIL_LOOKBACK_MS.
const LIVE_TAIL_MAX_AGE_MS = 30 * 60 * 1000;

type PlaybackState = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

/**
 * Nest a flat group list (rows with parent_id, e.g. from the getTree CTE) into a
 * parent→children tree. Returns the list unchanged if it is already nested.
 */
function buildGroupTree(flat: any[]): any[] {
  if (!Array.isArray(flat) || flat.length === 0) return [];
  if (flat[0]?.children) return flat;
  const map: Record<string, any> = {};
  const roots: any[] = [];
  for (const g of flat) map[g.id] = { ...g, children: [] };
  for (const g of flat) {
    if (g.parent_id && map[g.parent_id]) map[g.parent_id].children.push(map[g.id]);
    else roots.push(map[g.id]);
  }
  return roots;
}

/** Recursive group-tree renderer — same UI as the Cameras page group filter. */
function renderGroupTree(groups: any[], selectedId: string | undefined, onSelect: (g: any) => void, level = 0) {
  return groups.map((g: any) => (
    <div key={g.id}>
      <button
        onClick={() => onSelect(g)}
        className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] transition-colors ${selectedId === g.id ? 'bg-brand-light font-semibold text-brand' : 'hover:bg-muted text-foreground'
          }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
      >
        <FolderIcon className="h-4 w-4 text-yellow-400 flex-shrink-0" />
        <span className="truncate">{g.name}</span>
      </button>
      {g.children && renderGroupTree(g.children, selectedId, onSelect, level + 1)}
    </div>
  ));
}

export default function RecordingsPage() {
  // `selectedNodeId` (the scope) and the node list's own loading flag are needed alongside the
  // resolved node: on a cold reload `selectedNode` is null for the frame before the node list
  // arrives, which is NOT the same thing as "no site is selected".
  const { selectedNode, selectedNodeId, loading: nodesLoading } = useNode();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cameras, setCameras] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  // camera_id → recording config, for the picker's per-camera mode badge. null until the
  // one bulk request lands, which is what makes the badge say "Checking…" rather than
  // asserting a mode it hasn't been told yet. `recConfigsFailed` is tracked separately
  // because "the request failed" must NOT collapse into "no row, so continuous" — that
  // would label every camera with a mode nobody confirmed, and over the tunnel this call
  // fails for reasons that have nothing to do with the cameras.
  const [recConfigs, setRecConfigs] = useState<Record<string, any> | null>(null);
  const [recConfigsFailed, setRecConfigsFailed] = useState(false);
  // The node's own mode list (config `options.modes`), so a card never offers one it rejects.
  const [recModes, setRecModes] = useState<string[] | undefined>(undefined);
  /** camera_id → { segment_count, total_size, oldest_segment, newest_segment }. */
  const [storageByCamera, setStorageByCamera] = useState<Record<string, any>>({});
  // Tracked separately from an empty map: over a tunnel this call fails for reasons that have
  // nothing to do with the cameras, and "we couldn't read it" must not render as "nothing
  // recorded".
  const [storageFailed, setStorageFailed] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [search, setSearch] = useState('');
  const filterColumn = useFilterColumn('recordings');
  const [cameraPage, setCameraPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const CAMERAS_PER_PAGE = 16;

  const [selectedCamera, setSelectedCamera] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    // A reload must come back to the day being reviewed, not to today — see syncUrl below.
    const fromUrl = searchParams.get('date');
    if (fromUrl && /^\d{4}-\d{2}-\d{2}$/.test(fromUrl)) return fromUrl;
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  // True while a `?camera=` link (usually a plain browser refresh of the playback view) is
  // still waiting to be resolved against the camera list. The camera picker must not render
  // in the meantime, or every refresh flashes the picker on its way back to the player.
  const [restoringDeepLink, setRestoringDeepLink] = useState(() => Boolean(searchParams.get('camera')));

  /**
   * The URL is the only state that survives a refresh, so what is open is written into it.
   * Replace rather than push: stepping through days must not bury the previous page under a
   * history entry per click.
   */
  const syncUrl = useCallback((cam: any, date: string) => {
    const camId = cam ? String(cam.id || cam.camera_id) : '';
    setSearchParams(camId ? { camera: camId, date } : {}, { replace: true });
  }, [setSearchParams]);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [segments, setSegments] = useState<any[]>([]);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  // Trigger markers + the camera's mode: timeline presentation only. On a triggered camera the
  // gaps are expected, and an unlabelled bar full of holes reads as a fault.
  const [recTriggers, setRecTriggers] = useState<any[]>([]);
  const [recMode, setRecMode] = useState<string | null>(null);
  const [rangeStart, setRangeStart] = useState('00:00');
  const [rangeEnd, setRangeEnd] = useState('23:59');

  /**
   * Fit the range bar to the footage the day actually holds (hour granularity, matching the
   * HH:MM inputs). Called on every segment load, which is what stops a range applied to one
   * camera from carrying over to the next — a stale window usually lands on an empty bar, and
   * a full 24h bar over twenty minutes of clips is impossible to scrub precisely.
   * Finalized segments only: the live tail's end moves, so it must not set the bar's edge.
   */
  const fitRangeToFootage = useCallback((segs: any[]) => {
    const fin = (segs || []).filter((s: any) => (s.status || 'finalized') === 'finalized');
    const pad = (n: number) => String(n).padStart(2, '0');
    let fs = '00:00';
    let fe = '23:59';
    if (fin.length) {
      const minStart = new Date(Math.min(...fin.map((s: any) => new Date(s.start_time).getTime())));
      const maxEnd = new Date(Math.max(...fin.map((s: any) => new Date(s.end_time).getTime())));
      fs = `${pad(minStart.getHours())}:00`;
      const feHour = maxEnd.getMinutes() > 0 || maxEnd.getSeconds() > 0 ? maxEnd.getHours() + 1 : maxEnd.getHours();
      fe = feHour >= 24 ? '23:59' : `${pad(feHour)}:00`;
      if (fe <= fs) fe = '23:59';
    }
    setRangeStart(fs); setRangeEnd(fe);
  }, []);

  // Unified playback: ONE seekable range VOD playlist played through hls.js.
  // Seeking is just a currentTime change on this single source — no per-segment reload.
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [activeSegId, setActiveSegId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const playbackRateRef = useRef(1);
  // 4x–16x: fill a real buffer before playing instead of stalling per chunk (see the shared
  // highSpeedPlayback). The gate owns element.playbackRate while it exists — it holds with rate 0.
  const gateRef = useRef<HighSpeedBufferGate | null>(null);
  // Keyframe-only rendition in use (from 8x — see the shared highSpeedPlayback). Read per request.
  const trickRef = useRef(false);
  const [gate, setGate] = useState<GateState>({ holding: false, progress: 1 });
  const applyRate = (v: HTMLVideoElement | null, r: number) => {
    if (gateRef.current) gateRef.current.setRate(r);
    else if (v) v.playbackRate = r;
  };
  const [currentTime, setCurrentTime] = useState<Date | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const filteredSegmentsRef = useRef<any[]>([]);
  const playbackSegsRef = useRef<any[]>([]);   // finalized segments with player-time offsets
  const playbackUrlRef = useRef<string | null>(null);
  const rafRef = useRef<number | null>(null);
  const seekDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelNudgeRef = useRef<(() => void) | null>(null);  // the stalled-seek watchdog (seekNudge)

  const [playbackState, setPlaybackState] = useState<PlaybackState>('idle');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isBuffering, setIsBuffering] = useState(false);
  const bufferTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSeekRef = useRef<number | null>(null);  // player-time (s) once source loads
  const hlsRef = useRef<Hls | null>(null);
  const recoverRef = useRef({ media: 0, ts: 0 });
  // Repeated failures on a SINGLE fragment. A permanently gone chunk (segment
  // deleted by retention, or a transcode that gave up) would otherwise make us
  // startLoad() forever — an endless "Buffering…" with no error shown.
  const fragFailRef = useRef({ url: '', count: 0, ts: 0 });
  // Identifies this player to the node's playback service (?sid= on chunk
  // requests). It uses this to tell which in-flight transcodes a seek abandoned
  // and cancel them, instead of letting them hog the node's GPU ahead of the
  // segment we now want. Scrubbing over H265 recordings otherwise ends in a
  // permanent "Buffering…".
  const sessionIdRef = useRef(
    `c${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`,
  );
  // User's intended play/pause state. hls.js refires `canplay` at every playlist
  // discontinuity (each recording is a segment boundary) and after seeks — without
  // this guard those events would force-resume a video the user had paused.
  const wantPlayingRef = useRef(false);

  // Clip download state — pick a start/end on the timeline, then export that range.
  const RANGE_DOWNLOAD_MAX_MIN = 60; // mirrors local backend CLIP_EXPORT_MAX_SECONDS
  const [downloading, setDownloading] = useState(false);

  const { hasPermission } = useAuth();
  const canDownload = hasPermission('recordings.download');
  // Per-card recording on/off + type. Without it the cards stay read-only (the node also enforces).
  const canConfigRecording = hasPermission('recordings.config');

  /* ═══════════════════════════════════════════
   * CORE: teardown + single-source playback
   * The whole range is one hls.js source (unified playlist through the
   * tunnel); seeking moves currentTime.
   * ═══════════════════════════════════════════ */

  const teardown = useCallback(() => {
    pendingSeekRef.current = null;
    if (seekDebounceRef.current) { clearTimeout(seekDebounceRef.current); seekDebounceRef.current = null; }
    cancelNudgeRef.current?.();
    cancelNudgeRef.current = null;
    const v = videoRef.current;
    if (v) v.pause();
    setPlaybackUrl(null);   // clearing the src tears down hls.js via the setup effect
    setIsPlaying(false);
    setActiveSegId(null);
    setLoadError(null);
  }, []);

  // player-time (s, contiguous across the playlist) ↔ wall-clock (Date), via the
  // finalized-segment offset table. Gaps in footage collapse in player-time, so a
  // seek into a gap snaps to the nearest segment.
  const wallToPlayer = useCallback((ms: number) => {
    const segs = playbackSegsRef.current;
    if (!segs.length) return 0;
    for (const p of segs) {
      if (ms >= p.wallStart && ms <= p.wallEnd) return p.playerStart + (ms - p.wallStart) / 1000;
    }
    let best = segs[0];
    let bestDist = Infinity;
    for (const p of segs) {
      const dist = ms < p.wallStart ? p.wallStart - ms : ms - p.wallEnd;
      if (dist < bestDist) { bestDist = dist; best = p; }
    }
    return ms > best.wallEnd ? Math.max(best.playerStart, best.playerEnd - 1) : best.playerStart;
  }, []);

  const playerToWall = useCallback((pt: number) => {
    const segs = playbackSegsRef.current;
    for (const p of segs) {
      if (pt >= p.playerStart && pt < p.playerEnd) return new Date(p.wallStart + (pt - p.playerStart) * 1000);
    }
    const last = segs[segs.length - 1];
    return last ? new Date(last.wallEnd) : null;
  }, []);

  // Start (or, if already loaded, seek within) playback at a given player-time.
  // Rapid jumps are debounced so a burst of seeks coalesces into the final position.
  const startPlayback = useCallback((playerTime = 0) => {
    if (!playbackSegsRef.current.length || !selectedNode || !selectedCamera) return;
    if (playbackUrlRef.current) {
      // Only flip to "playing" when the user actually intends to play — a seek/skip
      // issued while paused must move the playhead without auto-resuming.
      if (wantPlayingRef.current) { setIsPlaying(true); setPlaybackState('playing'); }
      if (seekDebounceRef.current) clearTimeout(seekDebounceRef.current);
      seekDebounceRef.current = setTimeout(() => {
        seekDebounceRef.current = null;
        const v = videoRef.current;
        if (v) {
          try { v.currentTime = Math.max(0, playerTime); } catch { /* not seekable yet */ }
          cancelNudgeRef.current?.();
          cancelNudgeRef.current = nudgeStalledSeek(v);
          if (wantPlayingRef.current) v.play().catch(() => { });
        }
      }, 120);
      return;
    }
    // Build the playlist URL fresh so it carries the current token. The window
    // MUST be the applied range bounds — the same filter playbackSegs mirrors —
    // or the backend could include an extra boundary segment and the
    // player-time ↔ wall-clock offset table would desync.
    const camId = selectedCamera.id || selectedCamera.camera_id;
    const token = localStorage.getItem('cloud_token') || '';
    const [y, mo, dy] = selectedDate.split('-').map(Number);
    const dayStart = new Date(y, mo - 1, dy, 0, 0, 0, 0);
    const [sh, sm] = rangeStart.split(':').map(Number);
    const [eh, em] = rangeEnd.split(':').map(Number);
    const start = new Date(dayStart.getTime() + (sh * 60 + (sm || 0)) * 60000).toISOString();
    const end = new Date(dayStart.getTime() + (eh * 60 + (em || 0)) * 60000).toISOString();
    const url = `${API_BASE}/nodes/${selectedNode.nodeId}/recordings/playlist/${camId}?start_time=${encodeURIComponent(start)}&end_time=${encodeURIComponent(end)}&token=${encodeURIComponent(token)}`;
    pendingSeekRef.current = playerTime > 0 ? playerTime : null;
    setPlaybackState('loading');
    setIsPlaying(true);
    setPlaybackUrl(url);
  }, [selectedNode, selectedCamera, selectedDate, rangeStart, rangeEnd]);

  // hls.js lifecycle bound to the single playback source.
  useEffect(() => {
    const video = videoRef.current;
    playbackUrlRef.current = playbackUrl;
    if (!playbackUrl || !video) return;

    if (Hls.isSupported()) {
      trickRef.current = isTrickRate(playbackRateRef.current);
      const hls = new Hls({
        enableWorker: true,
        // Start loading AT the requested position. The MANIFEST_PARSED seek below
        // runs before the MediaSource has a duration, so hls.js still fetched
        // fragment 0 (midnight) first — a whole wasted transcode on H265 cameras.
        startPosition: pendingSeekRef.current != null && pendingSeekRef.current > 0
          ? pendingSeekRef.current : -1,
        // Look-ahead scaled to the speed (30/60/30 at <= 2x); updated live on a rate change.
        ...rateBufferConfig(playbackRateRef.current),
        // Chunks transcode on demand on the node and travel through the WS
        // tunnel — generous timeouts/retries.
        manifestLoadingTimeOut: 60000,
        manifestLoadingMaxRetry: 4,
        manifestLoadingRetryDelay: 2000,
        levelLoadingTimeOut: 60000,
        fragLoadingTimeOut: 60000,
        fragLoadingMaxRetry: 6,
        fragLoadingRetryDelay: 500,
        maxBufferHole: 0.5,
        highBufferWatchdogPeriod: 1,
        nudgeOffset: 0.2,
        nudgeMaxRetry: 10,
        appendErrorMaxRetry: 5,
        xhrSetup: (xhr, url) => {
          // Tag chunk requests with this player's session id. xhrSetup is the last
          // point the URL can be changed — opening here means hls.js keeps our
          // URL (it only opens the request itself if xhrSetup didn't).
          if (url.includes('/hls/') && url.includes('.ts')) {
            const sep = url.includes('?') ? '&' : '?';
            xhr.open('GET', trickChunkUrl(`${url}${sep}sid=${sessionIdRef.current}`, trickRef.current), true);
          }
        },
      });
      hlsRef.current = hls;
      hls.loadSource(playbackUrl);
      hls.attachMedia(video);
      // At high speed, start converting the next segment while this one loads — same ?sid= as
      // xhrSetup (the chunk URLs already carry the tunnel token).
      const detachWarmer = attachNextSegmentWarmer(hls, Hls.Events.FRAG_LOADING, () => playbackRateRef.current, (url) => {
        const sep = url.includes('?') ? '&' : '?';
        return {
          url: url.includes('/hls/') && url.includes('.ts')
            ? trickChunkUrl(`${url}${sep}sid=${sessionIdRef.current}w`, trickRef.current) : url,
          headers: {},
        };
      });
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setPlaybackState('playing');
        setLoadError(null);
        if (pendingSeekRef.current != null) {
          try { video.currentTime = pendingSeekRef.current; } catch { /* not seekable yet */ }
          pendingSeekRef.current = null;
        }
        video.play().catch(() => { });
        applyRate(video, playbackRateRef.current);
      });
      // A chunk that loads clears the failure streak, so only *sustained*
      // failures on one chunk (never any success between) count toward giving up.
      hls.on(Hls.Events.FRAG_BUFFERED, () => {
        fragFailRef.current = { url: '', count: 0, ts: 0 };
      });
      const recover = recoverRef.current;
      hls.on(Hls.Events.ERROR, (_event: any, data: any) => {
        if (!data.fatal) {
          if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
            const v = videoRef.current;
            if (v) { try { v.currentTime += 0.1; } catch { /* ignore */ } }
          }
          return;
        }
        const status = data.response?.code || data.networkDetails?.status;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          if ((data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR
            || data.details === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT
            || data.details === Hls.ErrorDetails.MANIFEST_PARSING_ERROR)
            && (status === 404 || status === 410)) {
            hls.destroy();
            hlsRef.current = null;
            setPlaybackState('error');
            setLoadError('No recordings found for this range — they may have been removed by retention policy.');
            setIsPlaying(false);
            return;
          }
          // Fragment failure. Near the live edge or mid-transcode a 404/timeout
          // is genuinely transient, so keep resuming — but a chunk that never
          // recovers must not loop startLoad() forever, or the player freezes on
          // an endless spinner with no feedback. Count repeats on the SAME
          // fragment and surface a clear error once it's clearly permanent.
          const fragUrl = data.frag?.url || data.context?.url || '';
          const now = Date.now();
          const f = fragFailRef.current;
          if (fragUrl && fragUrl === f.url && now - f.ts < 30000) {
            f.count += 1;
          } else {
            f.url = fragUrl;
            f.count = 1;
          }
          f.ts = now;
          // 409 = the node cancelled that transcode because it believed this player
          // had seeked elsewhere. Normal while scrubbing, and it says nothing about
          // the recording, so it gets a long leash — but NOT the unlimited one it
          // used to get. An unbounded 409 retry is only ever correct if the node
          // eventually stops sending them, and when a cancel loop set in (each
          // request cancelling the other's transcode) this was what turned it into a
          // permanent silent "Buffering…". Scrubbing moves between fragments, so the
          // SAME fragment failing this many times in 30s is a stuck loop, not a user.
          const permanent = status === 404 || status === 410;
          if (f.count >= (permanent ? 6 : status === 409 ? 25 : 15)) {
            f.url = ''; f.count = 0;
            hls.destroy();
            hlsRef.current = null;
            setPlaybackState('error');
            setLoadError(permanent
              ? 'This part of the recording is unavailable — it may have been removed by the storage-retention policy, or the video failed to transcode.'
              : 'Playback stalled — this part of the recording could not be loaded.');
            setIsPlaying(false);
          } else {
            // Transient (tunnel hiccup, chunk still transcoding) — resume.
            try { hls.startLoad(); } catch { /* ignore */ }
          }
          return;
        }
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          const now = Date.now();
          if (now - recover.ts > 5000) recover.media = 0;
          recover.ts = now;
          recover.media += 1;
          if (recover.media <= 1) {
            hls.recoverMediaError();
          } else if (recover.media === 2) {
            try { hls.swapAudioCodec(); } catch { /* ignore */ }
            hls.recoverMediaError();
          } else {
            hls.destroy();
            hlsRef.current = null;
            setPlaybackState('error');
            setLoadError('Playback error — please reload.');
            setIsPlaying(false);
          }
          return;
        }
        hls.destroy();
        hlsRef.current = null;
        setPlaybackState('error');
        setLoadError('Fatal HLS playback error');
        setIsPlaying(false);
      });
      return () => {
        detachWarmer();
        hls.destroy();
        if (hlsRef.current === hls) hlsRef.current = null;
        video.pause();
      };
    }
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS
      video.src = playbackUrl;
      const onMeta = () => {
        if (pendingSeekRef.current != null) {
          video.currentTime = pendingSeekRef.current;
          pendingSeekRef.current = null;
        }
        setPlaybackState('playing');
        video.play().catch(() => { });
      };
      video.addEventListener('loadedmetadata', onMeta, { once: true });
      return () => {
        video.removeEventListener('loadedmetadata', onMeta);
        video.pause();
        video.removeAttribute('src');
        video.load();
      };
    }
    return undefined;
  }, [playbackUrl]);

  // One high-speed gate per source.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !playbackUrl) return undefined;
    const g = new HighSpeedBufferGate(v, setGate, () => hlsRef.current?.config.maxBufferLength);
    gateRef.current = g;
    g.setRate(playbackRateRef.current);
    return () => { g.destroy(); gateRef.current = null; setGate({ holding: false, progress: 1 }); };
  }, [playbackUrl]);

  // Buffering indicator + stall self-heal: tunnel-transcoded chunks can take
  // seconds after a seek — show a spinner instead of a frozen frame, and nudge
  // a "playing but not advancing" video back to life.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !playbackUrl) return;
    const show = () => {
      if (bufferTimerRef.current) return;
      bufferTimerRef.current = setTimeout(() => setIsBuffering(true), 300);
    };
    const hide = () => {
      if (bufferTimerRef.current) { clearTimeout(bufferTimerRef.current); bufferTimerRef.current = null; }
      setIsBuffering(false);
    };
    const onSeeked = () => { if (v.readyState >= 3) hide(); };
    v.addEventListener('waiting', show);
    v.addEventListener('seeking', show);
    v.addEventListener('stalled', show);
    v.addEventListener('playing', hide);
    v.addEventListener('canplay', hide);
    v.addEventListener('seeked', onSeeked);
    let lastTime = -1;
    let stalls = 0;
    const iv = setInterval(() => {
      // A high-speed gate hold is time deliberately NOT advancing — not a frozen stream.
      if (v.paused || v.seeking || v.readyState < 2 || gateRef.current?.isHolding) {
        lastTime = v.currentTime; stalls = 0; return;
      }
      if (lastTime >= 0 && Math.abs(v.currentTime - lastTime) < 0.05) {
        stalls += 1;
        if (stalls >= 2) setIsBuffering(true);
        if (stalls >= 3) {
          stalls = 0;
          try { v.currentTime = v.currentTime + 0.2; } catch { /* ignore */ }
          if (hlsRef.current) { try { hlsRef.current.startLoad(); } catch { /* ignore */ } }
          v.play().catch(() => { });
        }
      } else {
        stalls = 0;
        setIsBuffering(false);
      }
      lastTime = v.currentTime;
    }, 1000);
    return () => {
      v.removeEventListener('waiting', show);
      v.removeEventListener('seeking', show);
      v.removeEventListener('stalled', show);
      v.removeEventListener('playing', hide);
      v.removeEventListener('canplay', hide);
      v.removeEventListener('seeked', onSeeked);
      if (bufferTimerRef.current) { clearTimeout(bufferTimerRef.current); bufferTimerRef.current = null; }
      setIsBuffering(false);
      clearInterval(iv);
    };
  }, [playbackUrl]);

  /* ═══════════════════════════════════════════
   * Data fetching
   * ═══════════════════════════════════════════ */

  useEffect(() => {
    if (!selectedNode) { setCameras([]); setGroups([]); setRecConfigs(null); setLoading(false); return; }
    setLoading(true);
    // One request for every camera's recording config, so the picker can label each card with
    // what that camera is actually keeping. Deliberately NOT per-card: sixteen cards on screen
    // would otherwise mean sixteen tunnel round trips per page turn. Best-effort — a failure
    // leaves the cards unlabelled rather than blocking the picker.
    setRecConfigs(null);
    setRecConfigsFailed(false);
    // What each camera has STORED — the same one-request-for-all-cameras deal, so the cards can
    // say whether there is anything worth opening before the operator picks one.
    setStorageByCamera({});
    setStorageFailed(false);
    getRecordingStorageByCamera(selectedNode.nodeId)
      .then((res: any) => {
        const map: Record<string, any> = {};
        for (const c of res?.cameras || []) map[c.camera_id] = c;
        setStorageByCamera(map);
      })
      .catch(() => setStorageFailed(true));
    getRecordingConfigs(selectedNode.nodeId)
      .then((res: any) => {
        const map: Record<string, any> = {};
        for (const c of res?.configs || []) map[c.camera_id] = c;
        setRecConfigs(map);
        if (Array.isArray(res?.options?.modes) && res.options.modes.length) setRecModes(res.options.modes);
      })
      .catch(() => setRecConfigsFailed(true));
    Promise.all([
      getNodeCameras(selectedNode.nodeId).then((res: any) => Array.isArray(res) ? res : res?.data || []),
      getNodeGroups(selectedNode.nodeId).then((res: any) => Array.isArray(res) ? res : res?.data || []).catch(() => []),
    ]).then(([cams, grps]) => {
      setCameras(cams);
      setGroups(grps);
      // `?camera=<id>` opens that camera's playback directly instead of the camera picker —
      // both for a deep link from the live-view grid and for a plain refresh of the player.
      const camId = searchParams.get('camera');
      const cam = camId ? cams.find((c: any) => String(c.id || c.camera_id) === camId) : null;
      if (cam) {
        // Swap the object only when it is a different camera: this effect also re-runs when the
        // node's status flips, and a fresh object for the same camera would tear playback down
        // mid-view (the segment effect keys off `selectedCamera`).
        setSelectedCamera((prev: any) => (prev && String(prev.id || prev.camera_id) === camId ? prev : cam));
      } else if (camId) {
        // The id belongs to a different site (the operator switched sites with the player
        // open): keeping it would build this node's playlist URLs with another node's camera
        // id, so drop back to the picker instead.
        setSelectedCamera(null);
        setSearchParams({}, { replace: true });
      }
    }).catch(() => { setCameras([]); setGroups([]); })
      .finally(() => { setLoading(false); setRestoringDeepLink(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode, setSearchParams]);

  // Give up on a `?camera=` link only once no site can possibly resolve it. On a cold reload
  // the stored scope has not been turned into a node yet, so clearing the flag on the first
  // null `selectedNode` would show the picker (or "No site selected") for that frame.
  useEffect(() => {
    if (!restoringDeepLink) return;
    if (!selectedNode && (!selectedNodeId || !nodesLoading)) setRestoringDeepLink(false);
  }, [restoringDeepLink, selectedNode, selectedNodeId, nodesLoading]);

  useEffect(() => {
    if (!selectedNode || !selectedCamera) { setAvailableDates([]); return; }
    const camId = selectedCamera.id || selectedCamera.camera_id;
    getRecordingDates(selectedNode.nodeId, camId)
      .then((res: any) => setAvailableDates(Array.isArray(res) ? res : res?.data || res?.dates || []))
      .catch(() => setAvailableDates([]));
  }, [selectedNode, selectedCamera]);

  useEffect(() => {
    if (!selectedNode || !selectedCamera || !selectedDate) return;
    teardown();
    setPlaybackState('idle');
    setCurrentTime(null);

    setSegmentsLoading(true);
    const camId = selectedCamera.id || selectedCamera.camera_id;
    // Build local midnight / end-of-day so the backend query covers the correct IST day
    const [y, mo, dy] = selectedDate.split('-').map(Number);
    const dayStartLocal = new Date(y, mo - 1, dy, 0, 0, 0, 0);
    const dayEndLocal = new Date(y, mo - 1, dy, 23, 59, 59, 999);
    const startTime = dayStartLocal.toISOString();
    const endTime = dayEndLocal.toISOString();
    getRecordingSegments(selectedNode.nodeId, camId, startTime, endTime)
      .then((res: any) => {
        const d = res?.data || res;
        const segs = Array.isArray(d) ? d : d?.segments || [];
        setSegments(segs);
        fitRangeToFootage(segs);
      })
      .catch(() => { setSegments([]); fitRangeToFootage([]); })
      .finally(() => setSegmentsLoading(false));

    // Best-effort: a failure here must not affect playback, the timeline just loses its markers.
    getRecordingTriggers(selectedNode.nodeId, camId, startTime, endTime)
      .then((rows: any) => setRecTriggers(Array.isArray(rows) ? rows : []))
      .catch(() => setRecTriggers([]));
    getRecordingConfig(selectedNode.nodeId, camId)
      .then((res: any) => setRecMode(res?.config?.mode || null))
      .catch(() => setRecMode(null));
  }, [selectedNode, selectedCamera, selectedDate, teardown]);

  /* ═══════════════════════════════════════════
   * Filtered segments + refs
   * ═══════════════════════════════════════════ */

  const filteredSegments = useMemo(() => {
    if (!segments.length || !selectedDate) return [];
    const [y, mo, dy] = selectedDate.split('-').map(Number);
    const dayStart = new Date(y, mo - 1, dy, 0, 0, 0, 0); // local midnight
    const [sh, sm] = rangeStart.split(':').map(Number);
    const [eh, em] = rangeEnd.split(':').map(Number);
    const rStart = new Date(dayStart.getTime() + (sh * 60 + (sm || 0)) * 60000);
    const rEnd = new Date(dayStart.getTime() + (eh * 60 + (em || 0)) * 60000);
    return segments.filter((s: any) => {
      const st = new Date(s.start_time);
      const et = new Date(s.end_time);
      return et > rStart && st < rEnd;
    });
  }, [segments, selectedDate, rangeStart, rangeEnd]);

  useEffect(() => { filteredSegmentsRef.current = filteredSegments; }, [filteredSegments]);

  // Segments the unified playlist actually contains, with contiguous player-time
  // offsets. MUST mirror the local backend generatePlaylist (finalized +
  // OVERLAPPING [rStart, rEnd), ascending, then the LIVE TAIL — the in-progress
  // slot's fragments) so player-time ↔ wall-clock mapping lines up with the m3u8.
  const playbackSegs = useMemo(() => {
    if (!selectedDate) return [];
    const [y, mo, dy] = selectedDate.split('-').map(Number);
    const dayStart = new Date(y, mo - 1, dy, 0, 0, 0, 0);
    const [sh, sm] = rangeStart.split(':').map(Number);
    const [eh, em] = rangeEnd.split(':').map(Number);
    const rs = dayStart.getTime() + (sh * 60 + (sm || 0)) * 60000;
    const re = dayStart.getTime() + (eh * 60 + (em || 0)) * 60000;
    const rows: any[] = [];
    let acc = 0;
    segments
      .filter((s: any) => (s.status || 'finalized') === 'finalized')
      .filter((s: any) => new Date(s.end_time).getTime() > rs && new Date(s.start_time).getTime() < re)
      .sort((a: any, b: any) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
      .forEach((s: any) => {
        // playlist_duration, not duration: this table maps player-time onto
        // wall-clock, so it has to advance by the span the PLAYLIST gives each
        // segment. That is a whole number of GOP-length chunks and stops short of
        // the segment's tail, so it runs a little under the media duration —
        // using `duration` would drift a chunk per segment across the day.
        const dur = s.playlist_duration > 0 ? s.playlist_duration
          : (s.duration && s.duration > 0 ? s.duration : 300);
        rows.push({
          id: s.id,
          playerStart: acc,
          playerEnd: acc + dur,
          wallStart: new Date(s.start_time).getTime(),
          wallEnd: new Date(s.end_time).getTime(),
        });
        acc += dur;
      });

    // Live tail: the playlist appends the still-recording slot's closed fragments
    // after the finalized segments. One row per fresh in-progress segment, ending
    // "now" — the playlist is fetched at play time, so the real tail edge is
    // within a few seconds of this estimate (clicks inside the tail map by
    // wallStart offset, which is exact; only the clamped edge is approximate).
    const now = Date.now();
    segments
      .filter((s: any) => s.status === 'in-progress')
      .filter((s: any) => {
        const t = new Date(s.start_time).getTime();
        return t < re && now - t < LIVE_TAIL_MAX_AGE_MS;
      })
      .sort((a: any, b: any) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
      .forEach((s: any) => {
        const prevEnd = rows.length ? rows[rows.length - 1].wallEnd : rs;
        const wallStart = Math.max(new Date(s.start_time).getTime(), prevEnd, rs);
        const wallEnd = Math.min(now, re);
        if (wallEnd - wallStart < 1500) return;
        const dur = (wallEnd - wallStart) / 1000;
        rows.push({
          id: s.id,
          playerStart: acc,
          playerEnd: acc + dur,
          wallStart,
          wallEnd,
          live: true,
        });
        acc += dur;
      });

    return rows;
  }, [segments, selectedDate, rangeStart, rangeEnd]);

  useEffect(() => { playbackSegsRef.current = playbackSegs; }, [playbackSegs]);

  // Changing the applied range invalidates the current single-source playlist.
  useEffect(() => {
    teardown();
    setPlaybackState('idle');
    setCurrentTime(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart, rangeEnd]);

  // rAF loop for smooth playhead — maps single-source player-time → wall-clock.
  useEffect(() => {
    const tick = () => {
      const v = videoRef.current;
      // Not while a seek is queued or in flight: the video still reports the OLD position
      // then, and copying it back would undo the playhead the seek just set.
      if (v && !v.paused && v.readyState >= 2 && !v.seeking && !seekDebounceRef.current) {
        const pt = v.currentTime;
        const wall = playerToWall(pt);
        if (wall) setCurrentTime(wall);
        const cur = playbackSegsRef.current.find((p: any) => pt >= p.playerStart && pt < p.playerEnd);
        setActiveSegId(cur ? cur.id : null);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [playerToWall]);

  /* ═══════════════════════════════════════════
   * Event handlers
   * ═══════════════════════════════════════════ */

  /**
   * One card's switch or type picker, through the tunnel. A patch of just that field, so the
   * camera's tuned pre-roll / sensitivity / retention survive a type change; the badge updates
   * from what the node saved. Throws to the card, which shows the error in place.
   */
  async function changeCameraRecording(cameraId: string, patch: { enabled?: boolean; mode?: string }) {
    if (!selectedNode) return;
    const res: any = await updateRecordingConfig(selectedNode.nodeId, cameraId, patch);
    // A null map is a reload in flight (e.g. the site changed mid-save) — let it land instead.
    setRecConfigs((prev) => prev && ({
      ...prev,
      [cameraId]: { ...(prev[cameraId] || IMPLICIT_RECORDING_CONFIG), ...patch, ...(res?.config || {}) },
    }));
  }

  const selectCamera = useCallback((cam: any) => {
    if (selectedCamera && String(selectedCamera.id || selectedCamera.camera_id) === String(cam.id || cam.camera_id)) return;
    teardown();
    setPlaybackState('idle');
    setSelectedCamera(cam);
    syncUrl(cam, selectedDate);
    setSegments([]);
    setCurrentTime(null);
  }, [selectedCamera, teardown, syncUrl, selectedDate]);

  const handleCanPlay = useCallback(() => {
    const v = videoRef.current;
    if (v && pendingSeekRef.current != null) {
      const pt = pendingSeekRef.current;
      pendingSeekRef.current = null;
      try { v.currentTime = pt; } catch { /* not seekable yet */ }
      cancelNudgeRef.current?.();
      cancelNudgeRef.current = nudgeStalledSeek(v);
    }
    setPlaybackState('playing');
    setLoadError(null);
  }, []);

  const handleEnded = useCallback(() => {
    wantPlayingRef.current = false;
    setPlaybackState('idle');
    setIsPlaying(false);
  }, []);

  /** Timeline seek — single continuous source, so just move the playhead. */
  const handleSeek = useCallback((date: Date) => {
    if (!playbackSegsRef.current.length) return;
    setCurrentTime(date);
    startPlayback(wallToPlayer(date.getTime()));
  }, [startPlayback, wallToPlayer]);

  const playSegment = useCallback((seg: any) => {
    wantPlayingRef.current = true;
    const p = playbackSegsRef.current.find((x: any) => x.id === seg.id);
    startPlayback(p ? p.playerStart : 0);
  }, [startPlayback]);

  // Start at the range START: the first playlist segment can begin before the
  // applied range (overlap semantics) — seek so playback opens at the requested time.
  const playRange = useCallback(() => {
    const segs = playbackSegsRef.current;
    if (!segs.length || !selectedDate) return;
    const [y, mo, dy] = selectedDate.split('-').map(Number);
    const dayStart = new Date(y, mo - 1, dy, 0, 0, 0, 0);
    const [sh, sm] = rangeStart.split(':').map(Number);
    wantPlayingRef.current = true;
    startPlayback(wallToPlayer(dayStart.getTime() + (sh * 60 + (sm || 0)) * 60000));
  }, [startPlayback, wallToPlayer, selectedDate, rangeStart]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v || !playbackUrl) { playRange(); return; }
    if (v.paused) {
      wantPlayingRef.current = true;
      v.play().catch(() => { });
      setIsPlaying(true);
      setPlaybackState('playing');
    } else {
      wantPlayingRef.current = false;
      v.pause();
      setIsPlaying(false);
      setPlaybackState('paused');
    }
  };

  // Step UP one notch, wrapping 16× → 0.5×.
  const cycleSpeed = () => {
    setPlaybackRate((prev) => SPEEDS[(SPEEDS.indexOf(prev) + 1) % SPEEDS.length]);
  };

  // Step DOWN one notch, wrapping 0.5× → 16×. slowerSpeed feeds the button tooltip.
  const slowerSpeed = SPEEDS[(SPEEDS.indexOf(playbackRate) - 1 + SPEEDS.length) % SPEEDS.length];
  const slowDown = () => setPlaybackRate((prev) => SPEEDS[(SPEEDS.indexOf(prev) - 1 + SPEEDS.length) % SPEEDS.length]);

  // Jump the playhead ±seconds within the single continuous source. Reuses the
  // debounced seek path (startPlayback) and nudges the timeline label immediately.
  const skip = (deltaSec: number) => {
    const v = videoRef.current;
    if (!v || !playbackUrl) return;
    const dur = v.duration || 0;
    const target = Math.max(0, dur ? Math.min(v.currentTime + deltaSec, dur) : v.currentTime + deltaSec);
    const wall = playerToWall(target);
    if (wall) setCurrentTime(wall);
    startPlayback(target);
  };

  const triggerDownload = (url: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 1000);
  };

  // Clip = an exact [start, end] selected on the timeline (Select, or Shift+drag); the backend
  // trims boundary segments to those offsets (download-range). The selection chip carries the
  // Download button.
  const downloadClip = (sel: { start: number; end: number }, clear: () => void) => {
    const mins = (sel.end - sel.start) / 60000;
    if (!selectedNode || !selectedCamera || mins <= 0 || mins > RANGE_DOWNLOAD_MAX_MIN) return;
    setDownloading(true);
    try {
      const camId = selectedCamera.id || selectedCamera.camera_id;
      const token = localStorage.getItem('cloud_token') || '';
      const s = new Date(sel.start).toISOString();
      const e = new Date(sel.end).toISOString();
      triggerDownload(`${API_BASE}/nodes/${selectedNode.nodeId}/recordings/download-range/${camId}?start_time=${encodeURIComponent(s)}&end_time=${encodeURIComponent(e)}&token=${encodeURIComponent(token)}`);
      clear();
    } finally {
      setTimeout(() => setDownloading(false), 3000);
    }
  };

  const downloadSingleSegment = (segId: string) => {
    if (!selectedNode) return;
    const token = localStorage.getItem('cloud_token') || '';
    const url = `${API_BASE}/nodes/${selectedNode.nodeId}/recordings/download/${segId}?token=${encodeURIComponent(token)}`;
    triggerDownload(url);
  };

  useEffect(() => {
    playbackRateRef.current = playbackRate;
    applyRate(videoRef.current, playbackRate);
    applyRateToHls(hlsRef.current, playbackRate);
    // Crossing 8x swaps rendition in place (flush + reload from the playhead).
    const trick = isTrickRate(playbackRate);
    if (trick !== trickRef.current) {
      trickRef.current = trick;
      if (hlsRef.current && videoRef.current) switchRendition(hlsRef.current, Hls.Events.BUFFER_FLUSHING, videoRef.current);
    }
  }, [playbackRate]);

  // Enforce playbackRate when video starts playing (e.g. after segment change)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const enforce = () => applyRate(v, playbackRateRef.current);
    v.addEventListener('playing', enforce);
    v.addEventListener('loadeddata', enforce);
    return () => { v.removeEventListener('playing', enforce); v.removeEventListener('loadeddata', enforce); };
  }, []);

  const streamLoading = playbackState === 'loading';

  /* ═══════════════════════════════════════════
   * The shared control bar + timeline (same as the Recorded wall)
   * ═══════════════════════════════════════════ */
  const rangeStartMs = useMemo(() => {
    const [y, mo, dy] = selectedDate.split('-').map(Number);
    const [sh, sm] = rangeStart.split(':').map(Number);
    return new Date(y, mo - 1, dy, 0, 0, 0, 0).getTime() + (sh * 60 + (sm || 0)) * 60000;
  }, [selectedDate, rangeStart]);
  const rangeEndMs = useMemo(() => {
    const [y, mo, dy] = selectedDate.split('-').map(Number);
    const [eh, em] = rangeEnd.split(':').map(Number);
    return Math.max(rangeStartMs + 60000, new Date(y, mo - 1, dy, 0, 0, 0, 0).getTime() + (eh * 60 + (em || 0)) * 60000);
  }, [selectedDate, rangeEnd, rangeStartMs]);
  const hasFootage = playbackSegs.length > 0;
  // Before anything plays the playhead sits on the first footage in the range.
  const timeMs = currentTime ? currentTime.getTime() : (playbackSegs[0] ? Math.max(rangeStartMs, playbackSegs[0].wallStart) : rangeStartMs);

  const jumpPoints = useMemo(
    () => jumpPointsFor([segments], [recTriggers], rangeStartMs, rangeEndMs),
    [segments, recTriggers, rangeStartMs, rangeEndMs],
  );
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const say = useCallback((msg: string) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);
  const jump = (dir: 1 | -1) => {
    const target = jumpTarget(jumpPoints.points, timeMs, dir, rangeStartMs);
    if (target === null) { say(`No ${dir > 0 ? 'later' : 'earlier'} ${jumpPoints.kind} in this range`); return; }
    // Events and "footage resumes" sit on segment STARTS, so the lead-in usually lands in the
    // previous segment's tail or in the gap before it. Only lead in within the same segment.
    const moment = target + JUMP_LEAD_MS;
    const row = playbackSegs.find((r: any) => moment >= r.wallStart && moment < r.wallEnd);
    handleSeek(new Date(row && target < row.wallStart ? row.wallStart + 500 : target));
  };
  // ±seconds: within the loaded source when there is one, otherwise a seek that starts it.
  const skipBy = (deltaSec: number) => {
    if (playbackUrl) { skip(deltaSec); return; }
    if (hasFootage) handleSeek(new Date(Math.min(rangeEndMs, Math.max(rangeStartMs, timeMs + deltaSec * 1000))));
  };
  const applyRange = (next: { start: string; end: string }) => { setRangeStart(next.start); setRangeEnd(next.end); };

  // "Play range" on a timeline selection: it becomes the range (whole minutes — the range is
  // HH:mm), then plays from its start once the range change's teardown has landed
  // (playbackUrl back to null) — earlier, startPlayback still sees the old source in its ref.
  const autoPlayRef = useRef(false);
  const playSelection = (startMs: number, endMs: number) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    const [y, mo, dy] = selectedDate.split('-').map(Number);
    const day0 = new Date(y, mo - 1, dy).getTime();
    const s0 = new Date(Math.max(day0, startMs));
    const e0 = new Date(Math.min(day0 + 86400000 - 60000, endMs + 59999));
    const next = { start: `${pad(s0.getHours())}:${pad(s0.getMinutes())}`, end: `${pad(e0.getHours())}:${pad(e0.getMinutes())}` };
    if (!(next.start < next.end)) { say('Select at least a minute'); return; }
    autoPlayRef.current = true;
    applyRange(next);
  };
  useEffect(() => {
    if (!autoPlayRef.current || !playbackSegs.length || playbackUrl) return;
    autoPlayRef.current = false;
    playRange();
  }, [playRange, playbackSegs, playbackUrl]);

  usePlaybackKeys({ toggle: () => { if (hasFootage) togglePlay(); }, skip: skipBy, jump, enabled: !!selectedCamera });

  const sortedDates = useMemo(() => [...(availableDates || [])].map(String).sort(), [availableDates]);
  const prevDate = sortedDates.filter((d) => d < selectedDate).pop() || null;
  const nextDate = sortedDates.find((d) => d > selectedDate) || null;
  const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const laneStatus: TileStatus | undefined = segmentsLoading ? 'segments'
    : playbackState === 'loading' ? 'loading'
      : playbackState === 'error' ? 'error'
        : !filteredSegments.length ? 'no-footage' : undefined;

  // Camera-selection derived state. Hooks MUST stay above the early returns
  // below — a hook after a conditional return changes the hook count between
  // renders (React #310) the moment selectedNode/selectedCamera flips.
  const filteredCameras = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cameras.filter((c: any) => {
      const matchSearch = !q || c.name?.toLowerCase().includes(q);
      const matchGroup =
        selectedGroup === 'all' ||
        c.groups?.some((g: any) => g.id === selectedGroup) ||
        c.group_id === selectedGroup;
      return matchSearch && matchGroup;
    });
  }, [cameras, selectedGroup, search]);

  const totalCameraPages = Math.max(1, Math.ceil(filteredCameras.length / CAMERAS_PER_PAGE));
  // Clamped: a shrinking list (a camera deleted elsewhere) must not strand the picker on an
  // empty page past the end.
  const currentCameraPage = Math.min(cameraPage, totalCameraPages);
  const paginatedCameras = useMemo(() => {
    const start = (currentCameraPage - 1) * CAMERAS_PER_PAGE;
    return filteredCameras.slice(start, start + CAMERAS_PER_PAGE);
  }, [filteredCameras, currentCameraPage]);

  // Reset page when the filter (group or search) changes
  useEffect(() => { setCameraPage(1); }, [selectedGroup, search]);

  // The player's camera switcher. Cameras normalized to `id` — node rows may carry `camera_id`.
  const [panelCollapsed, togglePanel] = useStoredToggle(PANEL_KEY);
  const [segmentsCollapsed, toggleSegments] = useStoredToggle(SEGMENTS_PANEL_KEY, true);
  const panelCameras = useMemo(
    () => cameras.map((c: any) => ({ ...c, id: String(c.id || c.camera_id), name: c.name || String(c.id || c.camera_id) })),
    [cameras],
  );
  const playingSet = useMemo(
    () => new Set<string>(selectedCamera ? [String(selectedCamera.id || selectedCamera.camera_id)] : []),
    [selectedCamera],
  );


  /* ═══════════════════════════════════════════
   * NO NODE SELECTED
   * ═══════════════════════════════════════════ */
  if (!selectedNode) {
    // A site IS selected, the node list just hasn't arrived — saying "no site selected" here
    // would be a claim about the operator's choice rather than about the request.
    if (restoringDeepLink || (selectedNodeId && nodesLoading)) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-brand" />
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <FilmIcon className="h-12 w-12 mb-4 opacity-40" />
        <p className="text-[15px] font-medium">No site selected</p>
        <p className="mt-1 text-[13px]">Select a site from the title bar to view recordings.</p>
      </div>
    );
  }

  /* ═══════════════════════════════════════════
   * CAMERA SELECTION VIEW
   * ═══════════════════════════════════════════ */
  if (!selectedCamera) {
    if (loading || restoringDeepLink) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-brand" />
        </div>
      );
    }
    const activeFilters = (search ? 1 : 0) + (selectedGroup !== 'all' ? 1 : 0);
    return (
      <PageWithFilters
        filters={(
          <FilterColumn
            open={filterColumn.open}
            onToggle={filterColumn.toggle}
            label="Camera filters"
            activeCount={activeFilters}
            onClearAll={() => { setSearch(''); setSelectedGroup('all'); }}
          >
            <FilterSearch value={search} onChange={setSearch} placeholder="Search cameras…" />
            {/* Same group tree as the Cameras page */}
            <FilterSection
              title="Group"
              action={selectedGroup !== 'all' ? (
                <button type="button" onClick={() => setSelectedGroup('all')}
                  className="text-[11px] text-muted-foreground hover:text-foreground">Clear</button>
              ) : null}
            >
              <FilterCheck radio checked={selectedGroup === 'all'} onClick={() => setSelectedGroup('all')} label="All groups" />
              {renderGroupTree(buildGroupTree(groups), selectedGroup === 'all' ? undefined : selectedGroup, (g: any) => {
                setSelectedGroup(g?.id === selectedGroup ? 'all' : g.id);
              })}
              {groups.length === 0 && <p className="px-2 py-1.5 text-[12px] text-muted-foreground">No groups</p>}
            </FilterSection>
          </FilterColumn>
        )}
      >
        <PageTitle title="Recordings" subtitle={`${filteredCameras.length} camera${filteredCameras.length !== 1 ? 's' : ''}`} />
        {filteredCameras.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground">
            <VideoCameraIcon className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="text-[15px]">No cameras found</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {paginatedCameras.map((cam: any) => {
                const camId = cam.id || cam.camera_id;
                const store = storageByCamera[camId];
                return (
                  <RecordingCameraCard
                    key={camId}
                    name={cam.name}
                    online={isCameraOnline(cam.status)}
                    storage={store ?? (storageFailed ? null : undefined)}
                    onClick={() => selectCamera(cam)}
                    recording={recConfigs ? (recConfigs[camId] || IMPLICIT_RECORDING_CONFIG) : null}
                    recordingUnavailable={recConfigsFailed}
                    onRecordingChange={canConfigRecording && !recConfigsFailed ? (patch) => changeCameraRecording(camId, patch) : undefined}
                    modes={recModes}
                  />
                );
              })}
            </div>
            <ListPager page={currentCameraPage} pageSize={CAMERAS_PER_PAGE} total={filteredCameras.length} onPageChange={setCameraPage} itemLabel="cameras" />
          </>
        )}
      </PageWithFilters>
    );
  }

  /* ═══════════════════════════════════════════
   * RECORDING VIEWER (matches local node layout)
   * ═══════════════════════════════════════════ */
  // Height comes from the layout's <main>, not from a guess at the chrome above it: the app
  // header is 56px (h-14, not 64), <main> adds 8–12px of padding top AND bottom, the cloud
  // stacks notice bars and the local node can open the system-monitor panel. `100vh - 64px`
  // was therefore taller than the room available at every resolution, and the page's bottom
  // row — the transport — hung below the fold. `h-full` is exactly the space there is, and it
  // re-measures itself when a screen-share bar or an external display changes the viewport.
  return (
    <div className="flex flex-col h-full min-h-[26rem]">
      {toast && (
        <div className="fixed bottom-4 right-4 z-[60] rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-medium text-white shadow-lg">{toast}</div>
      )}
      {/* Camera name in the title bar; Back returns to the picker. */}
      <PageTitle title={selectedCamera.name} subtitle="Recordings">
        <Tip side="bottom" label="Back to the camera list">
          <button onClick={() => { teardown(); setPlaybackState('idle'); setSelectedCamera(null); syncUrl(null, selectedDate); }}
            className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" aria-label="Back to cameras">
            <ArrowLeftIcon className="h-3.5 w-3.5" />
            Cameras
          </button>
        </Tip>
      </PageTitle>

      {/* Days with footage — one slim strip, scrolls sideways when there are many */}
      {availableDates.length > 0 && (
        <div className="mb-2 flex shrink-0 items-center gap-1.5 overflow-x-auto scrollbar-hide rounded-lg border border-border bg-card px-2 py-1.5">
          {availableDates.map((d: string) => (
            <button key={d} onClick={() => { setSelectedDate(d); syncUrl(selectedCamera, d); }}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${selectedDate === d
                ? 'bg-brand text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}>
              {fmtDate(d)}
            </button>
          ))}
        </div>
      )}

      {/* Main content */}
      {/* One 8px gutter between all three columns: switcher · player · segments. */}
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 gap-2 overflow-hidden">
        {/* Camera switcher — the Recorded wall's grouped tree, click to load another camera
            on the same day without going back to the picker. */}
        <PlaybackCameraPanel
          variant="select"
          className="hidden lg:block"
          cameras={panelCameras}
          groups={groups}
          onWall={playingSet}
          onAdd={(c) => {
            const cam = cameras.find((x: any) => String(x.id || x.camera_id) === c.id);
            if (cam) selectCamera(cam);
          }}
          collapsed={panelCollapsed}
          onToggleCollapsed={togglePanel}
        />
        {/* Centre: Player + Timeline + Controls, one card */}
        <div className="flex flex-col flex-1 min-w-0 min-h-0 overflow-y-auto rounded-lg border border-border bg-card">
          {/* Video Player */}
          {/* The player takes the leftover height and nothing more. It used to be a fixed
              16:9 box: on a wide screen (an ultrawide, or a shared display whose viewport is
              wide but short) width×9/16 grew past the space left, and since the row above is
              overflow-hidden the timeline and transport were simply cut off — with no
              scrollbar to reach them. The <video> letterboxes itself (object-contain), so
              filling a bounded box keeps the picture's aspect either way. */}
          <div className="relative flex-1 min-h-[9rem] bg-black">
            {playbackUrl ? (
              /* Fills the bounded box above rather than imposing a 16:9 height of its own —
                 the <video> letterboxes with object-contain, so the aspect survives. */
              <div className="relative h-full w-full bg-black">
                {/* Click the picture to play/pause — where people reach first; the transport
                    row below is for scrubbing and speed. Routed through togglePlay so the
                    page's own state (button label, playbackState, and the wantPlaying intent
                    that decides whether a segment boundary resumes) stays authoritative.
                    Deliberately not focusable: the transport's Play button is the
                    keyboard-reachable equivalent. */}
                <div
                  className="h-full w-full cursor-pointer"
                  onClick={togglePlay}
                  title={isPlaying ? 'Click to pause' : 'Click to play'}
                >
                  <video
                    ref={videoRef}
                    className="h-full w-full object-contain bg-black"
                    muted
                    onEnded={handleEnded}
                    onCanPlay={() => {
                      const v = videoRef.current;
                      // Re-apply speed, but only auto-resume across chunk/segment
                      // boundaries when the user actually wants to be playing.
                      if (v) { applyRate(v, playbackRateRef.current); if (v.paused && wantPlayingRef.current) v.play().catch(() => { }); }
                      handleCanPlay();
                    }}
                    onPlay={() => { applyRate(videoRef.current, playbackRateRef.current); setIsPlaying(true); }}
                    onPause={() => setIsPlaying(false)}
                  />
                  {/* Buffering spinner — seek target chunk still transcoding on the node */}
                  {(isBuffering || gate.holding) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none z-10">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span className="text-[12px] text-white/70">
                          Buffering…{gate.holding ? ` ${Math.round(gate.progress * 100)}%` : ''}
                        </span>
                      </div>
                    </div>
                  )}
                  {/* Paused affordance — purely a hint. pointer-events-none so the click lands
                      on the surface handler above rather than being split between two. */}
                  {!isPlaying && !isBuffering && playbackState !== 'loading' && playbackState !== 'error' && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <div className="rounded-full bg-black/40 p-3">
                        <svg className="h-10 w-10 text-white/80" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center text-white/40">
                <div className="text-center space-y-2">
                  <VideoCameraIcon className="h-12 w-12 mx-auto opacity-30" />
                  <p className="text-[13px]">Select a time range and press play, or click a segment</p>
                </div>
              </div>
            )}
            {/* Loading overlay */}
            {streamLoading && playbackUrl && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span className="text-[12px] text-white/70">Loading segment from local node…</span>
                </div>
              </div>
            )}
            {/* Error overlay */}
            {playbackState === 'error' && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 z-10">
                <div className="flex flex-col items-center gap-3 max-w-xs text-center">
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="text-[13px] text-white/80">{loadError || 'Failed to load segment'}</span>
                  <button onClick={() => { setPlaybackState('idle'); setLoadError(null); teardown(); }}
                    className="px-3 py-1.5 rounded-lg bg-white/10 text-[12px] text-white/70 hover:bg-white/20 transition-colors">
                    Dismiss
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Timeline + controls — the same bar and timeline as the Recorded wall:
              readout · date · range · Select · duration ···· ⏮ «30 «10 ▶ 10» 30» ⏭ · speed */}
          <div className="shrink-0 border-t border-border bg-card p-1.5 sm:p-2">
            <MultiCameraTimeline
              lanes={[{
                key: String(selectedCamera.id || selectedCamera.camera_id),
                label: selectedCamera.name,
                color: '#f97316',
                segments: filteredSegments,
                triggers: recTriggers,
                status: laneStatus,
              }]}
              rangeStartMs={rangeStartMs}
              rangeEndMs={rangeEndMs}
              timeMs={timeMs}
              onSeek={(ms, final) => { if (final) handleSeek(new Date(ms)); else setCurrentTime(new Date(ms)); }}
              onPlayRange={playSelection}
              follow={isPlaying}
              dateLabel={new Date(rangeStartMs).toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              leading={(
                <>
                  <ClockReadout timeMs={timeMs} note={streamLoading ? 'loading…' : null} />
                  <DateStepper date={selectedDate} max={todayStr} onChange={(d) => { setSelectedDate(d); syncUrl(selectedCamera, d); }} prevDate={prevDate} nextDate={nextDate} />
                  <TimeRangePicker value={{ start: rangeStart, end: rangeEnd }} onApply={applyRange} footage={footageRangeOf([segments], selectedDate)} />
                </>
              )}
              trailing={(
                <TransportCluster
                  playing={isPlaying}
                  onToggle={togglePlay}
                  disabled={!hasFootage}
                  playDisabled={streamLoading}
                  onSkip={skipBy}
                  onPrevEvent={() => jump(-1)}
                  onNextEvent={() => jump(1)}
                  eventKind={jumpPoints.kind}
                  playbackRate={playbackRate}
                  speeds={SPEEDS}
                  onRateChange={setPlaybackRate}
                />
              )}
              selectionActions={canDownload ? (sel, clear) => {
                const tooLong = (sel.end - sel.start) / 60000 > RANGE_DOWNLOAD_MAX_MIN;
                return (
                  <Tip label={tooLong ? `Clip too long — the most one download can be is ${RANGE_DOWNLOAD_MAX_MIN} min` : 'Download this range as one MP4'}>
                  <button onClick={() => downloadClip(sel, clear)} disabled={tooLong || downloading}
                    className="flex h-7 items-center gap-1 bg-emerald-600 px-2 text-[11px] font-semibold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40">
                    <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                    <span>
                      {downloading ? 'Preparing…' : tooLong ? `Max ${RANGE_DOWNLOAD_MAX_MIN}m` : 'Download'}
                    </span>
                  </button>
                  </Tip>
                );
              } : undefined}
            />
          </div>
        </div>

        {/* Right: Segments — time span, length, download */}
        <RecordingSegmentsPanel
          segments={filteredSegments}
          activeId={activeSegId}
          playing={isPlaying}
          loading={segmentsLoading}
          subtitle={`${rangeStart} – ${rangeEnd} · ${selectedDate}`}
          onPlay={playSegment}
          onDownload={canDownload ? (seg) => downloadSingleSegment(String(seg.id)) : undefined}
          collapsed={segmentsCollapsed}
          onToggleCollapsed={toggleSegments}
        />
      </div>
    </div>
  );
}

